// src/LoadServiceAreaData.js
import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Paper, 
  Button, 
  Box, 
  Alert,
  AlertTitle,
  CircularProgress,
  Snackbar,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoIcon from '@mui/icons-material/Info';

const LoadServiceAreaData = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');

  // Handle file selection
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
    setUploadResult(null);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (!file) {
      showAlert('Please select a file first', 'error');
      return;
    }

    // Check if file is a valid spreadsheet
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
      'application/csv',
      'application/x-csv',
      'text/x-csv',
      'text/comma-separated-values'
    ];
    
    if (!validTypes.includes(file.type)) {
      showAlert('Please upload a valid Excel or CSV file', 'error');
      return;
    }

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || ""}/api/service-areas/import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error uploading file');
      }
      
      const result = await response.json();
      setUploadResult(result);
      showAlert('File uploaded and processed successfully', 'success');
      
      // Clear file input
      setFile(null);
      const fileInput = document.getElementById('service-area-file-input');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error uploading file:', error);
      showAlert(`Failed to upload file: ${error.message}`, 'error');
    } finally {
      setUploading(false);
    }
  };

  // Show alert
  const showAlert = (message, severity) => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  // Close alert
  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertOpen(false);
  };

  return (
    <Container>
      <Typography variant="h5" sx={{ mb: 3, mt: 2 }}>
        Load Service Area Data
      </Typography>
      
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 3 }}>
          <AlertTitle>Important Note</AlertTitle>
          <Typography variant="body1">
            The columns in your spreadsheet are assumed to be in the same order as the database table:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary="1. Zip Code (required)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="2. County (required)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. Territory" />
            </ListItem>
            <ListItem>
              <ListItemText primary="4. Service Area" />
            </ListItem>
            <ListItem>
              <ListItemText primary="5. Notes" />
            </ListItem>
          </List>
          <Typography variant="body2">
            The first row is assumed to be a header row and will be skipped.
          </Typography>
        </Alert>
        
        <Box 
          sx={{ 
            border: '2px dashed #ccc', 
            borderRadius: '4px', 
            p: 3, 
            textAlign: 'center',
            backgroundColor: '#f9f9f9'
          }}
        >
          <input
            id="service-area-file-input"
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          <label htmlFor="service-area-file-input">
            <Button 
              variant="contained" 
              component="span"
              startIcon={<CloudUploadIcon />}
              sx={{ mb: 2 }}
            >
              Browse for Spreadsheet
            </Button>
          </label>
          
          {file && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1">
                Selected file: <strong>{file.name}</strong> ({(file.size / 1024).toFixed(2)} KB)
              </Typography>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleUpload}
                disabled={uploading}
                sx={{ mt: 2 }}
              >
                {uploading ? <CircularProgress size={24} sx={{ mr: 1 }} /> : 'Upload & Import'}
              </Button>
            </Box>
          )}
          
          {!file && !uploading && (
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              Select an Excel (.xlsx, .xls) or CSV file to import service area data
            </Typography>
          )}
        </Box>
      </Paper>
      
      {/* Results Section */}
      {uploadResult && (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Import Results
          </Typography>
          
          <Alert severity="success" sx={{ mb: 2 }}>
            Successfully imported {uploadResult.imported} service area records.
          </Alert>
          
          {uploadResult.errors && uploadResult.errors.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Some rows had issues:</AlertTitle>
              <List dense>
                {uploadResult.errors.map((error, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={error} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
          
          <Typography variant="body2" sx={{ mt: 2 }}>
            You can now go to the Service Area Management page to view and edit the imported data.
          </Typography>
        </Paper>
      )}
      
      {/* Alert Snackbar */}
      <Snackbar open={alertOpen} autoHideDuration={6000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity={alertSeverity} sx={{ width: '100%' }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default LoadServiceAreaData;